import * as React from 'react';
import DateRangeModel from 'src/model/dateRange.model';
import TrackingSearchBar from '../components/TrackingSearchBar';
import TrackingTable from '../components/TrackingMobileTable';
import TrackingModel from 'src/model/trackingInfo.model';
import SlideMenu from 'src/components/common/SlideMenu';
import { enableScroll, disableScroll } from 'src/utils/documentInteraction';
import DateRangeSeparated from 'src/components/common/DateRangeSeparated';
import SortCard from 'src/components/common/SortCard';
import { Moment } from 'moment';
import Button from 'src/components/common/Button';
import TablePaginator from 'src/components/common/TablePagination';

import './TrackingInfoMobile.scss';

export interface ITrackMobileProps {
  t: any;
  filters: ITrackFilters;
  trackings: TrackingModel[];
  pagination: {
    pageNumber: number;
    pageSize: number;
    totalPages: number;
    totalElements: number;
  };
  handleSearchText(e: any): void;
  handleOnClickSearch(): void;
  setMobileFilters(filters: ITrackFilters): void;
  handlePagination(type: any): void;
}

interface ITrackMobileState {
  filtersMenuIsOpen: boolean;
  applyFilters: {
    dates: DateRangeModel | null;
    sortBy: string;
    sortIn: 'asc' | 'desc';
  };
}

interface ISort {
  sortBy: string;
  sortIn: 'asc' | 'desc';
  label: string;
}

interface ITrackFilters {
  dates: DateRangeModel | null;
  search: string;
  sortBy: string;
  sortIn: SortType;
}

type SortType = 'ASC' | 'DESC' | 'AUTO';

class TrackingMobile extends React.Component<
  ITrackMobileProps,
  ITrackMobileState
> {
  constructor(props: ITrackMobileProps) {
    super(props);
    this.state = {
      filtersMenuIsOpen: false,
      applyFilters: {
        ...this.props.filters,
        sortIn: 'desc'
      }
    };
  }

  private openFiltersMenu = () => {
    this.setState({ filtersMenuIsOpen: true });
    disableScroll();
  };

  private closeFiltersMenu = () => {
    this.setState({ filtersMenuIsOpen: false });
    enableScroll();
  };

  private setDates = (dates: { start: Moment; end: Moment }) => {
    const applyFilters = { ...this.state.applyFilters, dates };
    this.setState({ applyFilters });
  };

  private setSort = (sort: ISort) => {
    const { sortBy, sortIn } = sort;
    const applyFilters = {
      ...this.state.applyFilters,
      sortBy,
      sortIn
    };
    this.setState({ applyFilters });
  };

  private getSorts = (t: any): JSX.Element[] => {
    const sortBy: ISort[] = [
      {
        sortBy: 'date',
        sortIn: 'desc',
        label: t('tracking.filters.orderDateAsc')
      },
      {
        sortBy: 'date',
        sortIn: 'asc',
        label: t('tracking.filters.orderDateDesc')
      }
    ];
    const sorts: JSX.Element[] = [];
    sortBy.map((sort: ISort, index) => {
      sorts.push(
        <SortCard
          key={index}
          sort={sort}
          setSort={this.setSort}
          active={
            this.state.applyFilters.sortBy === sort.sortBy &&
            this.state.applyFilters.sortIn === sort.sortIn
          }
        />
      );
    });

    return sorts;
  };

  private saveFilters = () => {
    const { dates, sortIn } = this.state.applyFilters;
    const validDates = dates ? (dates.start && dates.end ? dates : null) : null;
    const validSortIn: SortType = sortIn === 'asc' ? 'ASC' : 'DESC';
    const filters = {
      ...this.state.applyFilters,
      search: this.props.filters.search,
      dates: validDates,
      sortIn: validSortIn
    };
    this.props.setMobileFilters(filters);
    const date: DateRangeModel | null = null;
    const applyFilters = {
      page: 1,
      dates: date,
      sortBy: 'date',
      sortIn
    };
    this.setState({ applyFilters }, () => {
      this.closeFiltersMenu();
    });
  };

  public render() {
    const { t, pagination } = this.props;
    const headers = [
      {
        label: t('tracking.mobileHeaders.orderNumber'),
        sortBy: '',
        key: 'orderNumber'
      },
      {
        label: t('tracking.mobileHeaders.confirmationNumber', {
          interpolation: { escapeValue: false }
        }),
        sortBy: '',
        key: 'confirmationNumber'
      },
      {
        label: t('tracking.mobileHeaders.trackNumber'),
        sortBy: '',
        key: 'trackNumber'
      }
    ];
    return (
      <div className="rp-tracking-mobile">
        <div
          className="filters-and-sort--open-filters"
          onClick={this.openFiltersMenu}
        >
          <i className="rpds-sort" />
          <span className="filters-text">{t('common.filtersAndSorting')}</span>
        </div>
        <TrackingSearchBar
          mode="Mobile"
          filters={this.props.filters}
          handleSearchText={this.props.handleSearchText}
          handleOnClickSearch={this.props.handleOnClickSearch}
        />
        <div className="rp-product-tracking-mobile-paginator">
          <TablePaginator
            handlePagination={this.props.handlePagination}
            pagination={pagination}
          />
        </div>
        <TrackingTable headers={headers} trackings={this.props.trackings} />
        <SlideMenu
          mounted={this.state.filtersMenuIsOpen}
          level={11 + 30}
          goBackTitle={t('common.backToResults')}
          handleCloseMenu={() => {
            this.closeFiltersMenu();
          }}
        >
          <div className="rp-tracking--filters">
            <div className="rp-filters-container">
              <span className="content--filter-by">{t('common.filterBy')}</span>
              <div className="content--calendars">
                <DateRangeSeparated
                  dates={null}
                  onDatesSelected={selectedDates => {
                    this.setDates(selectedDates);
                  }}
                />
              </div>
              <div className="content--sort-by">
                <div className="sort-by--header">
                  <span>{`${t('common.sortBy')}:`}</span>
                  <i className="rpds-expand-more" />
                </div>
                <div className="sort-by--sorts">{this.getSorts(t)}</div>
              </div>
            </div>
            <Button
              buttonClass="primary-custom-button"
              label={t('common.saveFilters')}
              textTransform="uppercase"
              width="100%"
              height="48px"
              action={() => {
                this.saveFilters();
              }}
            />
          </div>
        </SlideMenu>
      </div>
    );
  }
}

export default TrackingMobile;
